import { useState } from "react";
import {
  TrendingUp, TrendingDown, Minus, Sparkles, CircleDot,
  Youtube, Globe, MessageSquare, ExternalLink, ChevronDown,
} from "lucide-react";

/* ── colour palette ── */
const ACCENT   = "#E8522A";
const ACCENT2  = "#F2744A";
const MUTED_BG = "#F0EDE9";
const SOURCE_COLORS = {
  youtube_video:   "#FF4444",
  youtube_comment: "#FF8A65",
  parallel:        "#3B82F6",
  web:             "#8B5CF6",
};
const SOURCE_LABELS = {
  youtube_video:   "YouTube Video",
  youtube_comment: "Audience Comment",
  parallel:        "Web Article",
  web:             "Web",
};

export const DIRECTIONS = {
  rising:    { label: "Rising",    icon: TrendingUp,  cls: "text-emerald-700", bg: "bg-emerald-50 border border-emerald-200/70" },
  emerging:  { label: "Emerging",  icon: Sparkles,    cls: "text-accent",     bg: "bg-accent/10 border border-accent/25" },
  stable:    { label: "Stable",    icon: Minus,       cls: "text-muted-foreground", bg: "bg-elevated border border-border" },
  declining: { label: "Declining", icon: TrendingDown,cls: "text-rose-600",   bg: "bg-rose-50 border border-rose-200/60" },
  saturated: { label: "Saturated", icon: CircleDot,   cls: "text-muted-foreground", bg: "bg-elevated border border-border" },
};

export function TrendBadge({ direction }) {
  const d = DIRECTIONS[direction] || DIRECTIONS.stable;
  const Icon = d.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-bold ${d.cls} ${d.bg} shadow-2xs`}
      data-testid={`trend-${direction}`}>
      <Icon className="h-3.5 w-3.5" /> {d.label}
    </span>
  );
}

export function InferenceTag({ kind = "inference" }) {
  const map = {
    observed:     { label: "Observed",    cls: "border-border text-muted-foreground" },
    inference:    { label: "AI Inference",cls: "border-accent/40 text-accent" },
    insufficient: { label: "Low data",    cls: "border-border text-muted-foreground" },
  };
  const m = map[kind] || map.inference;
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${m.cls}`}>
      {m.label}
    </span>
  );
}

export function ScorePills({ relevance, confidence, evidence_strength }) {
  const bar = (val) => (
    <div className="h-1.5 w-12 overflow-hidden rounded-full bg-elevated">
      <div className="h-full rounded-full bg-accent" style={{ width: `${val || 0}%` }} />
    </div>
  );
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1" data-testid="score-pills">
      {[["Relevance", relevance], ["Confidence", confidence], ["Evidence", evidence_strength]].map(([l, v]) => (
        <div key={l} className="flex items-center gap-1.5">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{l}</span>
          {bar(v)}
          <span className="text-xs font-bold tabular-nums text-foreground">{v || 0}</span>
        </div>
      ))}
    </div>
  );
}

/* ── visual evidence panel — collapsible dropdown (closed by default) ── */
export function EvidencePanel({ ids = [], evidence = {}, testid, defaultOpen = false }) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const items = ids.map((id) => evidence[id]).filter(Boolean);
  if (items.length === 0) return null;

  return (
    <div className="mt-3 pt-3 border-t border-border/60" data-testid={testid}>
      {/* Dropdown toggle button */}
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        data-testid="evidence-toggle"
        className="w-full flex items-center justify-between py-1.5 px-2.5 rounded-lg border border-border bg-elevated/30 hover:bg-elevated hover:border-border transition-colors text-left cursor-pointer"
      >
        <span className="text-xs font-medium text-muted-foreground">
          Supporting Evidence ({items.length})
        </span>
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <span>{isOpen ? "Hide" : "View"}</span>
          <ChevronDown
            className={`h-3.5 w-3.5 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
          />
        </div>
      </button>

      {/* Citation list (only shown when dropdown is expanded) */}
      {isOpen && (
        <div className="mt-2.5 space-y-2">
          {items.map((it, i) => {
            const type  = it.source_type || it.source || "web";
            const Icon  = type === "youtube_video"   ? Youtube :
                          type === "youtube_comment" ? MessageSquare : Globe;

            return (
              <div
                key={it.id || i}
                className="flex items-start gap-2.5 rounded-lg border border-border bg-surface p-2.5 transition-colors hover:border-accent/30"
              >
                <div className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground">
                  <Icon className="h-3.5 w-3.5" />
                </div>

                <div className="flex-1 min-w-0">
                  <a
                    href={it.url}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-start justify-between gap-2 text-xs font-medium text-foreground hover:text-accent transition-colors"
                  >
                    <span className="line-clamp-2 leading-snug">{it.title || "Untitled Signal"}</span>
                    <ExternalLink className="h-3 w-3 shrink-0 text-muted-foreground opacity-60 mt-0.5" />
                  </a>

                  <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px] text-muted-foreground">
                    <span className="uppercase tracking-wider font-semibold text-[9px]">
                      {SOURCE_LABELS[type] || type}
                    </span>
                    {it.published_at && (
                      <span>{new Date(it.published_at).toLocaleDateString()}</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function relativeTime(iso) {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.round(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} hr ago`;
  return `${Math.round(h / 24)} d ago`;
}
